<template>
  <v-row align="center" justify="start" dense>
    <v-col cols="12">
      <h2>{{ title }}</h2>
    </v-col>
    <v-col cols="4">
      <!-- Here a preview of the uploaded file (or current file) can be shown -->
      <slot name="preview" />

      <figure v-show="files[0]?.thumb" class="py-4">
        <v-img :src="files[0]?.thumb" width="180" height="auto" />
      </figure>

      <v-card v-show="upload && upload.dropActive">
        <v-card-title>
          {{ $t('components.common.uploadForm.dropToUpload') }}</v-card-title
        >
      </v-card>
    </v-col>
    <v-col cols="8">
      <v-card
        v-show="files.length === 0 || (props.multiple && files.length > 0)"
        variant="text"
      >
        <v-card-subtitle>{{ dropFileText }}</v-card-subtitle>
        <v-card-actions>
          <v-btn
            variant="tonal"
            prepend-icon="mdi-file"
            tag="label"
            :for="name"
            >{{ $t('components.common.uploadForm.chooseFile') }}</v-btn
          >
        </v-card-actions>
      </v-card>
      <template v-for="file in files" :key="file.id">
        <v-card variant="flat" class="ma-4">
          <v-row justify="center" align="center" dense>
            <v-col cols="12" md="6">
              <template v-if="files.length > 1">
                <figure v-show="file?.thumb" class="py-4">
                  <v-img :src="file.thumb" width="180" height="auto" />
                </figure>
              </template>
              <v-card-title>{{
                $t('components.common.uploadForm.name')
              }}</v-card-title>
              <v-card-subtitle>
                {{ file.name }}
              </v-card-subtitle>
              <progress
                v-if="
                  file.active ||
                  (file.progress !== '0.00' && file.progress != '100.00')
                "
                :class="{
                  'is-danger': file.error,
                  'is-success': file.success,
                }"
                :modelValue="file.success ? 100 : parseInt(file.progress)"
                max="100"
              >
                {{ file.progress }}%
              </progress>
              <v-card-title>
                {{ $t('components.common.uploadForm.size') }}
              </v-card-title>
              <v-card-subtitle v-show="!!file.size">
                {{ file.size }}
              </v-card-subtitle>
              <template v-if="file.success || file.active || file.error">
                <v-card-title
                  >{{ $t('components.common.uploadForm.status') }}
                </v-card-title>
                <v-card-subtitle v-if="file.error">
                  <v-chip variant="elevated" color="red">{{
                    file.error
                  }}</v-chip>
                </v-card-subtitle>
                <v-card-subtitle v-else-if="file.success"
                  ><v-chip variant="elevated" color="green"
                    >Ok</v-chip
                  ></v-card-subtitle
                >
                <v-card-subtitle v-else-if="file.active"
                  ><v-chip variant="elevated" color="green"
                    >active</v-chip
                  ></v-card-subtitle
                >
              </template>
            </v-col>
            <v-col cols="6"> </v-col>
            <v-card-actions class="w-100">
              <v-btn
                v-if="!upload || !upload.active"
                type="button"
                color="error"
                variant="elevated"
                prepend-icon="mdi-close"
                @click.prevent="files = files.filter((f) => f.id !== file.id)"
              >
                {{ $t('components.common.cancel') }}
              </v-btn>
              <v-btn
                v-else
                type="button"
                color="red"
                variant="elevated"
                prepend-icon="mdi-stop-circle-outline"
                @click.prevent="upload.active = false"
              >
                {{ $t('components.common.uploadForm.stopUpload') }}
              </v-btn>
            </v-card-actions>
          </v-row>
        </v-card>
      </template>
    </v-col>

    <div>
      <VueUploadComponent
        ref="upload"
        v-model="files"
        :name="name"
        :post-action="postAction"
        :extensions="extensions"
        :accept="accept"
        :multiple="multiple"
        :directory="directory"
        :size="maxSize || 0"
        :thread="thread < 1 ? 1 : thread > 5 ? 5 : thread"
        :headers="headers"
        :drop="drop"
        :drop-directory="dropDirectory"
        :add-index="addIndex"
        :chunk-enabled="chunkEnabled"
        :chunk="chunk"
        :data="additionalData"
        @update:model-value="updatedValue"
        @input-filter="inputFilter"
        @input-file="inputFile"
      />
    </div>
    <v-row v-if="files.length > 0 && !autoUpload"> </v-row>
    <slot name="file-restrictions" />
  </v-row>
</template>

<script setup lang="ts">
import { computed, Ref, ref, watch, withDefaults } from 'vue'
import { useI18n } from 'vue-i18n'
import VueUploadComponent from 'vue-upload-component'
import { useStore } from 'vuex'

import { getTranslation } from '@/lang/setup'
import { TransientBaseObject } from '@/models/core/base'
const store = useStore()

export interface UploadState {
  active: boolean
  blob: string
  data: Object
  error: string
  file: File
  fileObject: boolean
  headers: Object
  id: string
  name: string
  postAction: string
  progress: string
  putAction: string
  response: TransientBaseObject
  size: number
  speed: number
  success: boolean
  thumb: string
  timeout: number
  type: string
  xhr: XMLHttpRequest
}

declare var window: any

type UploadFormProps = {
  title: string
  uploadUrl?: string
  name?: string
  multiple?: boolean
  maxSize?: number
  extensions?: string[]
  accept?: string
  dropFileText?: string
  autoUpload?: boolean
  confirmUploadMessage?: string
  additionalData?: Record<string, unknown>
}

const props = withDefaults(defineProps<UploadFormProps>(), {
  uploadUrl: '/api/v1/avatar/',
  name: 'image',
  multiple: true,
  maxSize: 1024 * 1024 * 100, // 100 MB
  extensions: undefined,
  accept: 'image/png,image/gif,image/jpeg,image/webp',
  dropFileText: getTranslation('components.common.uploadForm.dropToUpload'),
  autoUpload: false,
  confirmUploadMessage: null,
  additionalData: () => ({}),
})

const directory = false
const drop = true
const dropDirectory = true
const addIndex = false
const chunkEnabled = false
const chunk = {
  action: props.uploadUrl,
  minSize: 1 * 1024 * 1024,
  maxActive: 3,
  maxRetries: 3,
}
const thread = 3
const postAction = props.uploadUrl
const headers = {
  ...store.state.global.axiosConfig.headers,
}

const i18n = useI18n()
const files: Ref<any[]> = ref([])

const upload = ref(null)

const emit = defineEmits(['success', 'input'])

watch(files, () => {
  let success = true
  files.value.forEach((file) => {
    if (!file.success) {
      success = false
    }
  })
  if (success) {
    emit('success')
  }
})

const uploadedFiles = computed(() => {
  return files.value.filter((file) => {
    return file.success
  })
})

function updatedValue(value: UploadState) {
  emit('input', value)
}

// add, update, remove File Event
function inputFile(newFile, oldFile) {
  if (newFile && oldFile) {
    if (newFile.progress !== oldFile.progress) {
      // progress
    }
    if (newFile.error && !oldFile.error) {
      // error
      if (newFile.response[props.name]) {
        let errorResponse = newFile.response[props.name]
        if (errorResponse instanceof Array) {
          newFile.error = errorResponse[0]
        } else if (errorResponse instanceof String) {
          newFile.error = errorResponse
        }
      }
      if (newFile.error === 'extension') {
        newFile.error = i18n.t('components.common.uploadForm.invalidExtension')
      } else if (newFile.response && newFile.response.image) {
        if (newFile.response.image instanceof Array) {
          newFile.error = newFile.response.image[0]
        } else if (newFile.response.image instanceof String) {
          newFile.error = newFile.response.image
        }
      }
      console.log('error', newFile)
    }
    if (newFile.success && !oldFile.success) {
      // success
    }
  }
  // Automatically activate upload
  if (
    Boolean(newFile) !== Boolean(oldFile) ||
    oldFile.error !== newFile.error
  ) {
    if (props.autoUpload && !upload.value.active) {
      upload.value.active = true
    }
  }
}

function inputFilter(newFile, oldFile, prevent) {
  if (newFile && !oldFile) {
    // Before adding a file
    // Filter system files or hide files
    if (/(\/|^)(Thumbs\.db|desktop\.ini|\..+)$/.test(newFile.name)) {
      return prevent()
    }
    // Filter php html js file
    if (/\.(php5?|html?|jsx?)$/i.test(newFile.name)) {
      return prevent()
    }
  }
  if (newFile && (!oldFile || newFile.file !== oldFile.file)) {
    // Create a blob field
    newFile.blob = ''
    let URL = window.URL || window.webkitURL
    if (URL && URL.createObjectURL) {
      newFile.blob = URL.createObjectURL(newFile.file)
    }
    // Thumbnails
    newFile.thumb = ''
    if (newFile.blob && newFile.type.substr(0, 6) === 'image/') {
      newFile.thumb = newFile.blob
    }
  }
}

defineExpose({
  uploadedFiles,
})
</script>

<style lang="scss" scoped></style>
